import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { STORAGE_KEYS } from "../utils/StorageKeys";
import { User } from "../screens/MainScreen";

interface UserContextType {
    currentUser: User | null;
    setCurrentUser: (user: User | null) => void;
    isAdmin: boolean;
    setIsAdmin: (isAdmin: boolean) => void;
    isAuthenticated: boolean;
    handleLogout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error("useUserContext must be used within a UserProvider");
    }
    return context;
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
    const [currentUser, setCurrentUserState] = useState<User | null>(null);
    const [isAdmin, setIsAdminState] = useState<boolean>(false);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

    // Load from localStorage on mount
    useEffect(() => {
        const savedUser = localStorage.getItem(STORAGE_KEYS.USER);
        const adminAuth = localStorage.getItem(STORAGE_KEYS.USER_TYPE);
        const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (savedUser) {
            try {
                setCurrentUserState(JSON.parse(savedUser));
            } catch {
                setCurrentUserState(null);
            }
        }
        setIsAdminState(adminAuth === "admin");
        setIsAuthenticated(!!accessToken);
    }, []);

    const setCurrentUser = (user: User | null) => {
        setCurrentUserState(user);
        setIsAuthenticated(!!user);
    };

    const setIsAdmin = (admin: boolean) => {
        setIsAdminState(admin);
        localStorage.setItem(STORAGE_KEYS.USER_TYPE, admin ? "admin" : "user");
    };

    const handleLogout = () => {
        setCurrentUserState(null);
        setIsAdminState(false);
        setIsAuthenticated(false);
        localStorage.clear();
    };

    return (
        <UserContext.Provider
            value={{ currentUser, setCurrentUser, isAdmin, setIsAdmin, isAuthenticated, handleLogout }}
        >
            {children}
        </UserContext.Provider>
    );
};
